import { useMemo } from 'react'
import {
  Home,
  History,
  User,
  ClipboardList,
  Gamepad2,
  Trophy,
  Gift,
  Target,
  School,
  GraduationCap,
  UserPlus,
  TrendingUp,
  Calendar,
  Calculator,
  UserCheck,
  Map,
  Play,
  Sparkles,
  Waypoints,
  Building2,
  Users
} from 'lucide-react'
import { isInstitutionOrg } from '../utils/roles'

/**
 * Builds the student/coach sidebar navigation model (the grouped `navGroups` and the `bottomItems`).
 * Pure data — no store access, no React-Router, no hooks beyond memoization — so the Sidebar component
 * keeps the subscriptions, mode-filtering, and render while this owns the (large, conditional) menu
 * definition. Memoized on its inputs so the arrays are stable between renders.
 *
 * @param {object} params
 * @param {object} params.user                       current user (isAdmin, isCoach, accountType)
 * @param {boolean} params.isCoach
 * @param {boolean} params.isGuardian
 * @param {boolean} params.canViewClassKnowledgeMap  coach/admin/org-staff
 * @param {boolean} params.hasCoaches                has an active or pending coach relationship
 * @param {boolean} params.classManagementEnabled    feature flag
 * @param {boolean} params.adaptiveTeachingEnabled   adaptive_teaching feature flag
 * @param {boolean} params.tutorGroupsEnabled        tutor_groups feature flag (Tutoring Pods)
 * @param {boolean} params.calculatorEnabled         feature flag
 * @param {number}  params.coachingActionCount       dashboard badge (pending coaching actions)
 * @param {number}  params.coachingNotificationCount Coaching badge
 * @param {number}  params.coachNotificationCount    My Coach badge
 * @param {object|null} params.activeOrganization    active org; its `type` gates institution-only items
 * @returns {{ navGroups: Array, bottomItems: Array }}
 */
export function useSidebarNavItems({
  user,
  isCoach,
  isGuardian,
  canViewClassKnowledgeMap,
  hasCoaches,
  classManagementEnabled,
  adaptiveTeachingEnabled,
  tutorGroupsEnabled,
  calculatorEnabled,
  coachingActionCount,
  coachingNotificationCount,
  coachNotificationCount,
  activeOrganization = null
}) {
  const navGroups = useMemo(
    () => [
      {
        // LEARN: individual (student) actions. Educator/extended actions live in the TEACH group
        // below so the two audiences read as distinct sections (and the Student/Coach mode toggle
        // keeps each to the right role).
        label: 'LEARN',
        items: [
          // Pending coaching actions (invitations/authorizations/agreements) badge the dashboard,
          // where the corresponding banners live, for students and guardians.
          {
            id: 'dashboard',
            name: 'Dashboard',
            icon: Home,
            href: '/dashboard',
            badge: coachingActionCount
          },
          // Hide Start Challenge for guardians
          ...(!isGuardian
            ? [
                {
                  id: 'challenge-type',
                  name: 'Start Challenge',
                  icon: Play,
                  href: '/challenge-type'
                }
              ]
            : []),
          // Assigned practices for students who participate in tutoring (have a coach)
          ...(hasCoaches && !isGuardian && !isCoach
            ? [
                {
                  id: 'assigned-practice',
                  name: 'Assigned Practice',
                  href: '/tutor-practice/assigned',
                  icon: ClipboardList
                }
              ]
            : []),
          // Show My Coach only for students with active coach relationships
          ...(hasCoaches && !isGuardian
            ? [
                {
                  id: 'my-coach',
                  name: 'My Coach',
                  href: '/my-coach',
                  icon: UserCheck,
                  badge: coachNotificationCount
                }
              ]
            : []),
          // Hide Challenge History and Focused Practice for guardians
          ...(!isGuardian
            ? [
                { id: 'history', name: 'Challenge History', icon: History, href: '/history' },
                {
                  id: 'focused-practice',
                  name: 'Focused Practice',
                  icon: Target,
                  href: '/focused-practice'
                },
                {
                  id: 'practice-games',
                  name: 'Practice Games',
                  icon: Gamepad2,
                  href: '/games'
                }
              ]
            : []),
          // Join class link for students (only if feature is enabled)
          ...(classManagementEnabled && !isGuardian && !user?.isCoach && !user?.isAdmin
            ? [{ id: 'join-class', name: 'Join Class', href: '/join/class', icon: UserPlus }]
            : []),
          { id: 'calendar', name: 'Calendar', href: '/calendar', icon: Calendar }
        ]
      },
      {
        // TEACH: educator / extended actions (Coach mode). Every id here is in COACH_ITEM_IDS
        // (utils/sidebarMode.js), so the group is filtered out in Student mode; the whole group is
        // dropped when empty (Sidebar hides zero-item groups), so pure students never see it.
        label: 'TEACH',
        items: [
          // Coaching hub for users who are coaches
          ...(isCoach
            ? [
                {
                  id: 'coach',
                  name: 'Coaching',
                  href: '/coach',
                  icon: GraduationCap,
                  badge: coachingNotificationCount
                }
              ]
            : []),
          // Aggregated cohort knowledge map for coaches / class instructors / org staff
          ...(canViewClassKnowledgeMap
            ? [
                {
                  id: 'class-knowledge-map',
                  name: 'Class Knowledge Map',
                  href: '/educator/knowledge-map',
                  icon: Map
                }
              ]
            : []),
          // Adaptive Teaching (Mastery Spiral): educator audience, ALSO gated by the
          // adaptive_teaching feature flag so the link only appears when the feature is enabled
          // (the route is flag-guarded and would otherwise redirect to the dashboard).
          ...(canViewClassKnowledgeMap && adaptiveTeachingEnabled
            ? [
                {
                  id: 'adaptive-teaching',
                  name: 'Adaptive Teaching',
                  href: '/educator/teaching',
                  icon: Waypoints
                }
              ]
            : []),
          // School Cockpit additionally requires an INSTITUTION as the active org: it shows one
          // organization's portfolio of Mastery Spirals, which is meaningless for a solo tutor on a
          // personal workspace or a guardian on a family org.
          ...(canViewClassKnowledgeMap &&
          adaptiveTeachingEnabled &&
          isInstitutionOrg(activeOrganization)
            ? [
                {
                  id: 'school-cockpit',
                  name: 'School Cockpit',
                  href: '/educator/cockpit',
                  icon: Building2
                }
              ]
            : []),
          // My Pod (Tutoring Pods): coaches only, gated by the tutor_groups feature flag. The /pod
          // route is flag-guarded server-side; hiding the link keeps the nav honest when it is off.
          ...(isCoach && tutorGroupsEnabled
            ? [
                {
                  id: 'my-pod',
                  name: 'My Pod',
                  href: '/pod',
                  icon: Users
                }
              ]
            : []),
          // Tutor Practices (frozen per-node practices) — coaches/admins author these
          ...(isCoach || user?.isAdmin
            ? [
                {
                  id: 'tutor-practices',
                  name: 'Tutor Practices',
                  href: '/tutor-practice',
                  icon: GraduationCap
                }
              ]
            : []),
          // Class management for coaches/instructors (only if feature is enabled)
          ...(classManagementEnabled && (user?.isCoach || user?.isAdmin)
            ? [{ id: 'my-classes', name: 'My Classes', href: '/coach/classes', icon: School }]
            : [])
        ]
      },
      ...(!isGuardian
        ? [
            {
              label: 'UNDERSTAND',
              items: [
                { id: 'progress', name: 'My Progress', icon: TrendingUp, href: '/progress' },
                { id: 'knowledge-map', name: 'Knowledge Map', icon: Map, href: '/knowledge-map' },
                {
                  id: 'score-reports',
                  name: 'Score Reports',
                  icon: ClipboardList,
                  href: '/score-reports'
                }
              ]
            }
          ]
        : []),
      ...(!isGuardian
        ? [
            {
              label: 'COMPETE',
              items: [
                { id: 'rankings', name: 'Rankings', icon: Trophy, href: '/rankings' },
                { id: 'referrals', name: 'Referrals', icon: Gift, href: '/referrals' }
              ]
            }
          ]
        : [])
    ],
    [
      user,
      isCoach,
      isGuardian,
      canViewClassKnowledgeMap,
      hasCoaches,
      classManagementEnabled,
      adaptiveTeachingEnabled,
      tutorGroupsEnabled,
      coachingActionCount,
      coachingNotificationCount,
      coachNotificationCount,
      activeOrganization
    ]
  )

  // NOTE: `my-classes` (TEACH group) and `join-class` (LEARN group) are role-filtered by
  // filterNavItemsByMode. They are intentionally NOT repeated in bottomItems — duplicating them
  // rendered each item twice in the sidebar.
  const bottomItems = useMemo(
    () => [
      { id: 'whats-new', name: "What's New", icon: Sparkles, href: '/whats-new' },
      { id: 'profile', name: 'Profile', icon: User, href: '/profile' },
      // Calculator - behind feature flag for gradual rollout
      ...(calculatorEnabled
        ? [{ id: 'calculator', name: 'Calculator', href: '/calculator', icon: Calculator }]
        : [])
    ],
    [calculatorEnabled]
  )

  return { navGroups, bottomItems }
}

export default useSidebarNavItems
